import type { EntityListColumn } from 'c-pagination';
import type { PopDisplays_JobStatus } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import React, { useCallback, useEffect } from 'react';
import { useDeleteEntityData } from 'c-data';
import { Alert, AutoGrid, Button, DialogV2 } from 'c-components';
import { NetworkRequestState } from 'c-data-layer';
import { useCommonTranslation } from 'c-translation';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useBoolean } from 'react-hanger';
import { canStopJob } from './lib';

const stopTaskPerms = [PermissionName.Admin];

const StopTaskCol: EntityListColumn<PopDisplays_JobStatus>['RenderCell'] = ({ entity }) => {
    const t = useCommonTranslation();
    const confirming = useBoolean(false);
    const { deleteEntity, getDeletingById, resetDeletingById } =
        useDeleteEntityData('PopDisplays_JobStatus');

    const deleteStatus = getDeletingById(entity.id);
    const isLoading = deleteStatus.state === NetworkRequestState.InProgress;

    useEffect(() => {
        if (deleteStatus.state === NetworkRequestState.Success) {
            confirming.setFalse();
        }
    }, [confirming, deleteStatus.state]);

    // The request state is keyed by entity id and outlives the dialog. Without clearing it, a row
    // whose previous attempt failed reopens showing that stale error, and one that succeeded
    // reopens only to be closed again immediately by the effect above.
    const onOpen = useCallback(() => {
        resetDeletingById(entity.id);
        confirming.setTrue();
    }, [resetDeletingById, entity.id, confirming]);

    const onConfirm = useCallback(() => {
        deleteEntity(entity.id);
    }, [deleteEntity, entity.id]);

    if (!canStopJob(entity)) return null;

    return (
        // The dialog renders inside a table row; stop its clicks reaching any row handler.
        <Box onClick={e => e.stopPropagation()}>
            <IfHasAllPermissions permissions={stopTaskPerms}>
                <Button sx={{ whiteSpace: 'nowrap' }} color="error" onClick={onOpen} size="small">
                    {t('Modules.PopDisplays.JobStatus.table.stopTaskLabel')}
                </Button>
                <DialogV2 open={confirming.value} onClose={confirming.setFalse}>
                    <Stack gap={3}>
                        <Typography align="center" variant="h2">
                            {t('Modules.PopDisplays.JobStatus.stopTask.title')}
                        </Typography>
                        <Typography align="center">
                            {t('Modules.PopDisplays.JobStatus.stopTask.description', {
                                name: entity.name,
                            })}
                        </Typography>
                        <AutoGrid justifyContent="center" gap={5}>
                            <Button color="error" onClick={onConfirm} disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    t('Modules.PopDisplays.JobStatus.stopTask.confirm')
                                )}
                            </Button>
                            <Button
                                color="primary"
                                onClick={confirming.setFalse}
                                disabled={isLoading}
                            >
                                {t('Modules.PopDisplays.JobStatus.stopTask.cancel')}
                            </Button>
                        </AutoGrid>
                        {deleteStatus.state === NetworkRequestState.Error && (
                            <Alert severity="error" variant="outlined">
                                {String(deleteStatus.error)}
                            </Alert>
                        )}
                    </Stack>
                </DialogV2>
            </IfHasAllPermissions>
        </Box>
    );
};

export default StopTaskCol;
